import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useIntl } from "react-intl";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useNotification } from "../../providers/NotificationProvider";
import {
  fetchTodoWorks,
  fetchDoingWorks,
  fetchDoneWorks,
  fetchWorkflowDetail,
  deleteTodoWork,
  fetchProject,
  fetchProgram,
} from "../../api/memberApi";
import AddWorkDialog from "./AddWorkDialog";
import WorkflowFormDialog from "./WorkflowFormDialog";

// ─── 作業テーブル ───────────────────────────────────────────────
function WorkTable({ works, onRowClick, onDelete, intl }) {
  if (works.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ mt: 2 }}>
        {intl.formatMessage({ id: "work.noWorks" })}
      </Typography>
    );
  }
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: "grey.50" }}>
            <TableCell sx={{ fontWeight: 600 }}>
              {intl.formatMessage({ id: "work.name" })}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {intl.formatMessage({ id: "work.member" })}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {intl.formatMessage({ id: "work.worker" })}
            </TableCell>
            {onDelete && <TableCell sx={{ width: 48 }} />}
          </TableRow>
        </TableHead>
        <TableBody>
          {works.map((work) => (
            <TableRow
              key={work.workId}
              hover
              sx={{ cursor: "pointer" }}
              onClick={() => onRowClick(work)}
            >
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {work.workName}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {work.memberName || "—"}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {work.workerName || "—"}
                </Typography>
              </TableCell>
              {onDelete && (
                <TableCell
                  align="right"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(work);
                  }}
                >
                  <Tooltip title={intl.formatMessage({ id: "common.delete" })}>
                    <IconButton size="small" color="error">
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── メインページ ────────────────────────────────────────────────
export default function WorkflowDetailPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const { workerId, workflowId } = useParams();
  const notify = useNotification();

  // WorkflowListPage から渡されたワークフロー情報（navigationState）
  const passedWorkflow = location.state?.workflow || null;

  const [workflow, setWorkflow] = useState(passedWorkflow);
  const [tabIndex, setTabIndex] = useState(0);
  const [todoWorks, setTodoWorks] = useState([]);
  const [doingWorks, setDoingWorks] = useState([]);
  const [doneWorks, setDoneWorks] = useState([]);
  const [loading, setLoading] = useState(true);

  // ダイアログ
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // 削除確認対象

  // 編集ダイアログ用
  const [projectId, setProjectId] = useState(null);
  const [programId, setProgramId] = useState(null);
  const [actionPlans, setActionPlans] = useState([]);

  // ── ワークフロー詳細取得（actionPlanId を補完する） ──
  const loadWorkflowDetail = useCallback(async () => {
    console.log("[WorkflowDetailPage] passedWorkflow =", passedWorkflow);
    console.log("[WorkflowDetailPage] passedWorkflow.actionPlanId =", passedWorkflow?.actionPlanId);
    // passedWorkflow に既に actionPlanId がある場合はスキップ
    if (passedWorkflow?.actionPlanId) return;
    try {
      const data = await fetchWorkflowDetail(workflowId);
      console.log("[WorkflowDetailPage] fetchWorkflowDetail response =", data);
      setWorkflow((prev) => ({
        ...prev,
        ...data,
        // prev の actionPlanId が有効な場合は上書きしない
        actionPlanId: data?.actionPlanId || prev?.actionPlanId,
      }));
    } catch (err) {
      console.error("[WorkflowDetailPage] fetchWorkflowDetail error =", err);
      // エンドポイントが存在しない場合は passedWorkflow をそのまま使う
    }
  }, [workflowId, passedWorkflow]);

  // ── 作業一覧の読み込み ──
  const loadWorks = useCallback(async () => {
    setLoading(true);
    try {
      const [todo, doing, done] = await Promise.all([
        fetchTodoWorks(workflowId).catch(() => []),
        fetchDoingWorks(workflowId).catch(() => []),
        fetchDoneWorks(workflowId).catch(() => []),
      ]);
      setTodoWorks(Array.isArray(todo) ? todo : []);
      setDoingWorks(Array.isArray(doing) ? doing : []);
      setDoneWorks(Array.isArray(done) ? done : []);
    } catch (err) {
      notify.apiError(err);
    } finally {
      setLoading(false);
    }
  }, [workflowId, notify]);

  // ── プロジェクト/プログラム + アクションプラン読み込み（編集ダイアログ用） ──
  useEffect(() => {
    const wf = passedWorkflow || workflow;
    const pid = wf?.projectId || null;
    const pgid = wf?.programId || null;
    setProjectId(pid);
    setProgramId(pgid);

    const loadParent = async () => {
      try {
        let parentData = null;
        if (pid) parentData = await fetchProject(pid);
        else if (pgid) parentData = await fetchProgram(pgid);
        const plans = parentData?.associatedActionPlans ?? [];
        setActionPlans(Array.isArray(plans) ? plans : []);
      } catch {
        // 取得失敗は無視
      }
    };
    if (pid || pgid) loadParent();
  }, [passedWorkflow, workflow]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadWorkflowDetail();
    loadWorks();
  }, [loadWorkflowDetail, loadWorks]);

  // ── 行クリック → 作業詳細へ ──
  const handleRowClick = (work, status) => {
    navigate(
      `/workers/${workerId}/workflows/${workflowId}/works/${work.workId}`,
      { state: { work, status } }
    );
  };

  // ── ToDo 削除 ──
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteTodoWork(deleteTarget.workId);
      notify.success(intl.formatMessage({ id: "work.deleteSuccess" }));
      setDeleteTarget(null);
      loadWorks();
    } catch (err) {
      notify.apiError(err);
    }
  };

  return (
    <Box>
      {/* ヘッダー */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/workers/${workerId}/workflows`)}
          variant="text"
        >
          {intl.formatMessage({ id: "common.back" })}
        </Button>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {workflow?.workflowName ||
              intl.formatMessage({ id: "workflow.detail.title" })}
          </Typography>
          {workflow?.businessState && (
            <Chip
              label={intl.formatMessage({
                id: `workflow.status.${workflow.businessState.toLowerCase()}`,
                defaultMessage: workflow.businessState,
              })}
              size="small"
              color={
                workflow.businessState === "Executing"
                  ? "primary"
                  : workflow.businessState === "Completed"
                  ? "success"
                  : "default"
              }
              sx={{ mt: 0.5 }}
            />
          )}
        </Box>
        <Button
          variant="outlined"
          startIcon={<EditOutlinedIcon />}
          size="small"
          onClick={() => setEditOpen(true)}
        >
          {intl.formatMessage({ id: "common.edit" })}
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* タブ */}
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)}>
              <Tab
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {intl.formatMessage({ id: "work.todo" })}
                    <Chip
                      label={todoWorks.length}
                      size="small"
                      color={tabIndex === 0 ? "primary" : "default"}
                    />
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {intl.formatMessage({ id: "work.doing" })}
                    <Chip
                      label={doingWorks.length}
                      size="small"
                      color={tabIndex === 1 ? "warning" : "default"}
                    />
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {intl.formatMessage({ id: "work.done" })}
                    <Chip
                      label={doneWorks.length}
                      size="small"
                      color={tabIndex === 2 ? "success" : "default"}
                    />
                  </Box>
                }
              />
            </Tabs>
          </Box>

          {/* ToDo タブ */}
          {tabIndex === 0 && (
            <Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  size="small"
                  onClick={() => setAddOpen(true)}
                >
                  {intl.formatMessage({ id: "work.add.button" })}
                </Button>
              </Box>
              <WorkTable
                works={todoWorks}
                onRowClick={(work) => handleRowClick(work, "Todo")}
                intl={intl}
              />
            </Box>
          )}

          {/* 実行中タブ */}
          {tabIndex === 1 && (
            <WorkTable
              works={doingWorks}
              onRowClick={(work) => handleRowClick(work, "Doing")}
              intl={intl}
            />
          )}

          {/* 完了タブ */}
          {tabIndex === 2 && (
            <WorkTable
              works={doneWorks}
              onRowClick={(work) => handleRowClick(work, "Done")}
              intl={intl}
            />
          )}
        </>
      )}

      {/* 作業追加ダイアログ */}
      <AddWorkDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={() => {
          setTabIndex(0);
          loadWorks();
        }}
        workflowId={workflowId}
        workerId={workerId}
        actionPlanId={workflow?.actionPlanId}
      />

      {/* ワークフロー編集ダイアログ */}
      <WorkflowFormDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSaved={() => {
          setEditOpen(false);
          loadWorkflowDetail();
        }}
        workflow={workflow}
        projectId={projectId}
        programId={programId}
        actionPlans={actionPlans}
      />

      {/* ToDo 削除確認ダイアログ */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {intl.formatMessage({ id: "work.deleteConfirm.title" })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {intl.formatMessage(
              { id: "work.deleteConfirm.message" },
              { workName: deleteTarget?.workName || "" }
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>
            {intl.formatMessage({ id: "common.cancel" })}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
          >
            {intl.formatMessage({ id: "common.delete" })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
